export const TENANT_HIDDEN_NAVIGATION_PATHS_KEY = 'tenantHiddenNavigationPaths';

const MAX_HIDDEN_NAVIGATION_PATHS = 300;
const MAX_NAVIGATION_PATH_LENGTH = 220;

const TENANT_NAVIGATION_LOCKED_PATHS = new Set([
  '/overview',
  '/tasks',
  '/settings',
  '/settings/modules',
  '/settings/organisation',
  '/settings/users',
  '/settings/security',
  '/help',
]);

export type TenantNavigationVisibilityState = 'locked' | 'tenant_visible' | 'tenant_hidden';

export type TenantNavigationVisibilityLedgerEntry = {
  href: string;
  state: TenantNavigationVisibilityState;
};

export function normalizeNavigationPath(path: unknown): string | null {
  if (typeof path !== 'string') return null;
  const trimmed = path.trim();
  if (!trimmed || trimmed.length > MAX_NAVIGATION_PATH_LENGTH) return null;
  const pathname = trimmed.split(/[?#]/)[0] || '/';
  let normalized = pathname.replace(/\/+/g, '/');
  if (!normalized.startsWith('/')) normalized = `/${normalized}`;
  if (normalized.length > 1 && normalized.endsWith('/')) normalized = normalized.slice(0, -1);
  if (!/^\/[A-Za-z0-9/_-]*$/.test(normalized)) return null;
  return normalized;
}

export function isTenantNavigationPathLocked(path: unknown): boolean {
  const normalized = normalizeNavigationPath(path);
  return normalized ? TENANT_NAVIGATION_LOCKED_PATHS.has(normalized) : false;
}

export function isTenantNavigationPathConfigurable(path: unknown): boolean {
  const normalized = normalizeNavigationPath(path);
  return Boolean(normalized && !TENANT_NAVIGATION_LOCKED_PATHS.has(normalized));
}

export function normalizeTenantHiddenNavigationPaths(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const value of input) {
    const normalized = normalizeNavigationPath(value);
    if (!normalized || TENANT_NAVIGATION_LOCKED_PATHS.has(normalized) || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    out.push(normalized);
    if (out.length >= MAX_HIDDEN_NAVIGATION_PATHS) break;
  }
  return out;
}

export function getTenantHiddenNavigationPathsFromBrandConfig(
  brandConfig: Record<string, unknown> | null | undefined,
): string[] {
  return normalizeTenantHiddenNavigationPaths(brandConfig?.[TENANT_HIDDEN_NAVIGATION_PATHS_KEY]);
}

function normalizedHiddenPathSet(
  hiddenPaths: readonly string[] | ReadonlySet<string> | null | undefined,
): Set<string> {
  if (!hiddenPaths) return new Set();
  const values = hiddenPaths instanceof Set ? [...hiddenPaths] : [...hiddenPaths];
  return new Set(normalizeTenantHiddenNavigationPaths(values));
}

export function isTenantNavigationPathHidden(
  href: unknown,
  hiddenPaths: readonly string[] | ReadonlySet<string> | null | undefined,
  options: { includeDescendants?: boolean | null } = {},
): boolean {
  const normalized = normalizeNavigationPath(href);
  if (!normalized || TENANT_NAVIGATION_LOCKED_PATHS.has(normalized)) return false;
  const hidden = normalizedHiddenPathSet(hiddenPaths);
  if (hidden.has(normalized)) return true;
  return (
    options.includeDescendants === true &&
    [...hidden].some((hiddenPath) => normalized.startsWith(`${hiddenPath}/`))
  );
}

export function isTenantNavigationPathVisible(
  href: unknown,
  hiddenPaths: readonly string[] | ReadonlySet<string> | null | undefined,
  options: { includeDescendants?: boolean | null } = {},
): boolean {
  return !isTenantNavigationPathHidden(href, hiddenPaths, options);
}

export function buildTenantNavigationVisibilityLedger(
  hrefs: Iterable<string>,
  hiddenPaths: readonly string[] | ReadonlySet<string> | null | undefined,
): TenantNavigationVisibilityLedgerEntry[] {
  const hidden = normalizedHiddenPathSet(hiddenPaths);
  const seen = new Set<string>();
  const entries: TenantNavigationVisibilityLedgerEntry[] = [];
  for (const href of hrefs) {
    const normalized = normalizeNavigationPath(href);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    entries.push({
      href: normalized,
      state: TENANT_NAVIGATION_LOCKED_PATHS.has(normalized)
        ? 'locked'
        : hidden.has(normalized)
          ? 'tenant_hidden'
          : 'tenant_visible',
    });
  }
  return entries.sort((a, b) => a.href.localeCompare(b.href));
}
